import type { Transaction } from "@rocicorp/zero";
import {
  canReadConnector,
  canUseConnector,
  type Context,
} from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { buildUserGroupMemberships, type GrantRow } from "../rbac/grantQueries";
import {
  type PersonalUsePolicy,
  type PersonalUseTarget,
  personalUseBlocked,
} from "../personalUse";
import { spacePersonalUseTarget } from "../personalUseGuard";
import {
  isSupplierAvailableFromGrants,
  principalHasSupplierPermissionFromGrants,
} from "../rbac/crossResourceAvailability";
import { connectorTypeOf } from "./index";
import { isDraft, type ConnectorLike } from "./isDraft";
import { matchAvailableApps } from "./matchAvailableApps";
import { suggestConnectorTypes } from "./suggestConnectorTypes";
import { apiKey } from "./apiKey/manifest";
import { findConnectorTypes } from "./findConnectorTypes";
import { ExpectedError } from "../expectedError";
import {
  SEARCH_CONNECTORS_PAGE_SIZE,
  type SearchConnectorsInput,
} from "./searchConnectorsSchemas";
import { connectorCreatePath } from "./connectorCreatePath";
import { toConnectorResponse } from "./connectorMetadata";

type ConnectorSearchQueryInput = Pick<
  SearchConnectorsInput,
  "access" | "sort" | "order"
> & {
  createdAfter?: number;
};

export function connectorSearchQuery(
  ctx: Context,
  input: ConnectorSearchQueryInput = {}
) {
  const order = input.order ?? "asc";
  let query = zql.connectedApps
    .where("fresh", false)
    .where(
      input.access === "readable" ? canReadConnector(ctx) : canUseConnector(ctx)
    )
    .orderBy(input.sort === "created_at" ? "createdAt" : "name", order)
    .orderBy("id", order)
    .related("oauthConnection");
  if (input.createdAfter !== undefined)
    query = query.where("createdAt", ">", input.createdAfter);
  return query;
}

export type SearchConnectorsScope =
  | { kind: "personal_chat"; personalChatId: string }
  | { kind: "workflow"; workflowId: string };

const NOTE_MAX = 80;

export function truncatedNote(app: {
  note?: string | null;
}): string | undefined {
  if (!app.note) return undefined;
  return app.note.length <= NOTE_MAX
    ? app.note
    : `${app.note.slice(0, NOTE_MAX)}…`;
}

type AvailabilityCandidate = {
  roleGrantsAsScope: readonly GrantRow[];
  personalUse: PersonalUsePolicy | null;
};

export type ConnectorAvailabilityScope =
  | { kind: "personal_chat"; userId: string; userGroupIds: readonly string[] }
  | {
      kind: "space";
      mode: Extract<PersonalUseTarget, { kind: "space" }>["mode"];
      grants: readonly GrantRow[];
      userGroupMemberships: ReadonlyMap<string, readonly string[]>;
    };

export function isConnectorAvailableInScope(
  app: AvailabilityCandidate,
  scope: ConnectorAvailabilityScope
): boolean {
  if (personalUseBlocked(app, scope)) return false;
  return scope.kind === "personal_chat"
    ? principalHasSupplierPermissionFromGrants(
        scope.userId,
        scope.userGroupIds,
        app.roleGrantsAsScope,
        "connected_app:use"
      )
    : isSupplierAvailableFromGrants(
        "space_uses_connected_apps",
        scope.grants,
        app.roleGrantsAsScope,
        scope.userGroupMemberships
      );
}

async function scopeAvailabilityCheck(
  tx: Pick<Transaction<Schema>, "run">,
  tenantId: string,
  userId: string,
  scope: SearchConnectorsScope
): Promise<(app: AvailabilityCandidate) => boolean> {
  if (scope.kind === "personal_chat") {
    const userGroupIds = (
      await tx.run(zql.roleGrants.where("userId", userId))
    ).map((g) => g.scopeId);
    return (app) =>
      isConnectorAvailableInScope(app, {
        kind: "personal_chat",
        userId,
        userGroupIds,
      });
  }
  const [workflow] = await tx.run(zql.workflows.where("id", scope.workflowId));
  if (!workflow) return () => false;
  const spaceId = workflow.spaceId;
  const [target, spaceGrants] = await Promise.all([
    spacePersonalUseTarget(tx, tenantId, spaceId),
    tx.run(
      zql.roleGrants
        .where("scopeId", spaceId)
        .related("customRole", (cr) => cr.related("rolePermissions"))
    ) as Promise<GrantRow[]>,
  ]);
  const directUserIds = [
    ...new Set(spaceGrants.map((g) => g.userId).filter(Boolean) as string[]),
  ];
  const memberships =
    directUserIds.length > 0
      ? buildUserGroupMemberships(
          (await tx.run(
            zql.roleGrants.where("userId", "IN", directUserIds)
          )) as { userId: string | null; scopeId: string }[]
        )
      : new Map<string, readonly string[]>();
  return (app) =>
    isConnectorAvailableInScope(app, {
      ...target,
      grants: spaceGrants,
      userGroupMemberships: memberships,
    });
}

// Both discovery (searchConnectors) and execute's turn-time resolution consume
// this, so the two can never disagree on what is available.
export async function availableAppsForScope(
  tx: Pick<Transaction<Schema>, "run">,
  ctx: Context,
  scope: SearchConnectorsScope,
  input: Omit<ConnectorSearchQueryInput, "access"> & {
    connectorId?: string;
  } = {}
) {
  let query = connectorSearchQuery(ctx, { ...input, access: "usable" });
  if (input.connectorId) query = query.where("id", input.connectorId);
  const [apps, isAvailable] = await Promise.all([
    tx.run(
      query.related("roleGrantsAsScope", (rg) =>
        rg.related("customRole", (cr) => cr.related("rolePermissions"))
      )
    ),
    scopeAvailabilityCheck(tx, ctx.tenantId, ctx.userId, scope),
  ]);
  return apps.filter((app) => !isDraft(app)).filter(isAvailable);
}

export async function inUseAppIds(
  tx: Pick<Transaction<Schema>, "run">,
  scope: SearchConnectorsScope
): Promise<Set<string>> {
  if (scope.kind === "personal_chat") {
    const rows = await tx.run(
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("refId", scope.personalChatId)
    );
    return new Set(rows.map((r) => r.connectedAppId));
  }
  const [chatRows, branches] = await Promise.all([
    tx.run(
      zql.connectedAppUsages
        .where("type", "workflow_chat")
        .where("refId", scope.workflowId)
    ),
    tx.run(
      zql.workflowBranches
        .where("workflowId", scope.workflowId)
        .where("headCommitId", "IS NOT", null)
    ),
  ]);
  const ids = new Set(chatRows.map((r) => r.connectedAppId));
  const headCommitIds = branches
    .map((b) => b.headCommitId)
    .filter((id): id is string => id != null);
  if (headCommitIds.length === 0) return ids;
  const rows = await tx.run(
    zql.connectedAppUsages
      .where("type", "commit_step")
      .where("refId", "IN", headCommitIds)
  );
  for (const r of rows) ids.add(r.connectedAppId);
  return ids;
}

export async function usableWorkflowApps(
  tx: Pick<Transaction<Schema>, "run">,
  ctx: Context,
  workflowId: string
) {
  const scope = { kind: "workflow", workflowId } as const;
  const usableIds = await inUseAppIds(tx, scope);
  if (usableIds.size === 0) return [];
  const available = await availableAppsForScope(tx, ctx, scope);
  return available.filter((app) => usableIds.has(app.id));
}

export function selectConnectors<T extends ConnectorLike & { id: string }>(
  candidates: readonly T[],
  input: Pick<
    SearchConnectorsInput,
    "access" | "search" | "targetUrl" | "offset"
  >,
  limit?: number
) {
  const configured = candidates.filter((app) => !isDraft(app));
  const { matched, urlFallback } = matchAvailableApps(configured, input, {
    urlFallback: input.access !== "readable",
  });

  const productOf = (app: { identifier: string }) =>
    connectorTypeOf(app.identifier)?.identifier ?? app.identifier;
  const offset = input.offset ?? 0;
  const page = matched.slice(
    offset,
    limit === undefined ? undefined : offset + limit
  );
  const nextOffset =
    offset + page.length < matched.length ? offset + page.length : null;
  const readableSuggestions = () => {
    if (!(input.search || input.targetUrl)) return [];
    const types = findConnectorTypes(input);
    return types.length ? types : [apiKey];
  };
  const suggestions =
    input.access === "readable"
      ? readableSuggestions()
      : suggestConnectorTypes(input, new Set(matched.map(productOf)));
  return {
    apps: page,
    totalMatched: matched.length,
    nextOffset,
    ...(urlFallback && { urlFallback }),
    ...(suggestions.length > 0 && { suggestions }),
  };
}

export async function searchConnectors({
  tx,
  ctx,
  scope,
  input,
}: {
  tx: Pick<Transaction<Schema>, "run">;
  ctx: Context;
  scope: SearchConnectorsScope | null;
  input: SearchConnectorsInput;
}) {
  if (input.access !== "readable" && !scope)
    throw new ExpectedError(
      "Usable connector search requires a workflow or personal chat scope"
    );
  const queryInput = {
    createdAfter:
      input.createdAfter === undefined
        ? undefined
        : Date.parse(input.createdAfter),
    sort: input.sort,
    order: input.order,
  };
  const [apps, inUse] = await Promise.all([
    input.access === "readable"
      ? tx.run(connectorSearchQuery(ctx, { ...queryInput, access: "readable" }))
      : availableAppsForScope(tx, ctx, scope!, queryInput),
    scope ? inUseAppIds(tx, scope) : null,
  ]);
  const limit =
    input.limit === null
      ? undefined
      : (input.limit ?? SEARCH_CONNECTORS_PAGE_SIZE);
  return {
    ...selectConnectors(apps, input, limit),
    inUse,
  };
}

export function connectorSearchResponse(
  result: Awaited<ReturnType<typeof searchConnectors>>,
  origin: string | null
) {
  return {
    connectors: result.apps.map((connector) => ({
      ...toConnectorResponse(connector),
      inUse: result.inUse?.has(connector.id) ?? null,
    })),
    totalMatched: result.totalMatched,
    nextOffset: result.nextOffset,
    urlFallback: result.urlFallback ?? false,
    suggestions: (result.suggestions ?? []).map((suggestion) => ({
      identifier: suggestion.identifier,
      name: suggestion.name,
      product: suggestion.identifier,
      tagLine: suggestion.tagLine,
      aiContext: suggestion.aiContext ?? null,
      createUrl: `${origin}${connectorCreatePath(suggestion.identifier)}`,
    })),
  };
}
